/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useMemo } from 'react'
import { Canvas } from '@react-three/fiber'
import { FlightRig } from './flight/FlightRig.tsx'
import { ScrollDriver } from './flight/ScrollDriver.tsx'
import { Floor } from './systems/Floor.tsx'
import { Props } from './systems/Props.tsx'
import { Monkeys } from './systems/Monkeys.tsx'
import { HeroApe } from './systems/HeroApe.tsx'
import { Glyphs } from './systems/Glyphs.tsx'
import { GlyphsNear } from './systems/GlyphsNear.tsx'
import { Tubes } from './systems/Tubes.tsx'
import { Sheets } from './systems/Sheets.tsx'
import { Solids } from './systems/Solids.tsx'
import { Aperture } from './systems/Aperture.tsx'
import { OrganizeLens } from './systems/OrganizeLens.tsx'
import { Copy } from './copy/Copy.tsx'
import { Fallback } from './Fallback.tsx'
import { DevPanel } from './DevPanel.tsx'
import { Q } from './quality.ts'
import { frame } from './flight/state.ts'

function webglOK(): boolean {
  try {
    const c = document.createElement('canvas')
    return !!(c.getContext('webgl2') || c.getContext('webgl'))
  } catch {
    return false
  }
}

export default function App() {
  const useFallback = useMemo(
    () => globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches || !webglOK(),
    [],
  )
  const dev = useMemo(() => globalThis.location.search.includes('dev'), [])

  // ?p=0.55 pins progress — used for reviewing/screenshotting specific beats
  useMemo(() => {
    const m = globalThis.location.search.match(/[?&]p=([\d.]+)/)
    if (m) {
      frame.devP = parseFloat(m[1])
      frame.p = frame.devP
    }
  }, [])

  if (useFallback) return <Fallback />

  return (
    <>
      {/* SPEC §2 — ~700vh of document; the scrollbar tells the truth */}
      <div style={{ height: '700vh' }} aria-hidden="true" />
      <div className="canvas-wrap" aria-hidden="true">
        {/* flat+linear: every material in the site is a raw ShaderMaterial authoring its
            colours in display space; ACES tone mapping would darken only the few built-in
            materials (the aperture went grey) and break the "white is brightest" law */}
        <Canvas
          flat
          linear
          dpr={[1, Q.dpr]}
          gl={{ antialias: true, powerPreference: 'high-performance', alpha: false }}
          camera={{ fov: 32, near: 0.1, far: 400, position: [0, 1.1, 6] }}
        >
          <FlightRig />
          <Floor />
          <Props />
          <Monkeys />
          <HeroApe />
          <Glyphs />
          <GlyphsNear />
          <Tubes />
          <Sheets />
          <Solids />
          <Aperture />
          <OrganizeLens />
        </Canvas>
      </div>
      <ScrollDriver />
      <Copy />
      {dev && <DevPanel />}
    </>
  )
}
